import axios from 'axios';
import { errorHandler } from '../services/errorHandler';
import { getCsrfToken, mightBeAuthenticated } from './cookieUtils';

const isDev = process.env.NODE_ENV === 'development';
const defaultBaseUrl = isDev ? 'http://localhost:5000/api' : 'https://rfbox-api.runasp.net/api';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || defaultBaseUrl,
  withCredentials: true,
  timeout: 30000,
});

// Attach CSRF header if cookie exists
api.interceptors.request.use((config) => {
  const csrf = getCsrfToken();
  if (csrf) {
    config.headers = config.headers || {};
    (config.headers as any)['X-XSRF-TOKEN'] = csrf;
  }
  // Attach Accept-Language
  try {
    const lang = document?.documentElement?.lang || localStorage.getItem('i18nextLng') || 'en';
    config.headers = config.headers || {};
    (config.headers as any)['Accept-Language'] = lang;
  } catch {}
  return config;
});

// Refresh token on 401
let isRefreshing = false;
let queue: Array<() => void> = [];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const url: string = (original?.url || '');
    const skipRefresh = (
      url.startsWith('/auth/login') ||
      url.startsWith('/auth/register') ||
      url.startsWith('/auth/confirm-email') ||
      url.startsWith('/auth/refresh')
    );
    
    if (error.response?.status === 401 && !original._retry && !skipRefresh) {
      original._retry = true;
      
      // Only attempt refresh if user might be authenticated
      // This prevents unnecessary refresh attempts when clearly not logged in
      if (!mightBeAuthenticated()) {
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        await new Promise<void>((resolve) => queue.push(resolve));
      } else {
        isRefreshing = true;
        try {
          await api.post('/auth/refresh');
          queue.forEach((fn) => fn());
          queue = [];
        } finally {
          isRefreshing = false;
        }
      }
      return api(original);
    }
    
    // Check for rate limit (429) FIRST - always show toast for rate limits
    if (error.response?.status === 429) {
      errorHandler.handleError(error, {
        showToast: true,
        showValidationErrors: false,
        logError: true,
        preventDuplicate: true
      });
      return Promise.reject(error);
    }
    
    // Use the enhanced error handler for all other errors
    // Skip showing toast for auth endpoints - let components handle their own messages
    const isAuthEndpoint = (
      url.startsWith('/auth/login') ||
      url.startsWith('/auth/register') ||
      url.startsWith('/auth/confirm-email')
    );
    
    // Don't show duplicate toasts - components will handle validation errors inline
    const hasValidationErrors = error.response?.data?.validationErrors || error.response?.data?.code === 'VALIDATION_ERROR';
    
    // For auth endpoints, don't show toast - let components handle their own messages
    // For other endpoints, show toast unless there are validation errors (which are shown inline)
    errorHandler.handleError(error, {
      showToast: !isAuthEndpoint && !hasValidationErrors,
      showValidationErrors: false, // Validation errors are shown inline in form fields
      logError: true,
      preventDuplicate: true
    });
    
    return Promise.reject(error);
  }
);

export default api;


